import { PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import {
  AirportOperationalData,
  MultiAirportDateRangePayloads,
  MultiAirportOperationalDataFailure,
} from "../models/airports/AirportConfig";
import {
  EMPTY_AIRPORT_OPERATIONAL_DATA,
  loadMultiAirportOperationalData,
} from "../services/multi-airport/MultiAirportOperationalDataService";
import { AuthHelper } from "../utils/api/AuthHelper";

export interface OperationalAirport {
  id?: string;
  code?: string;
  iataCode?: string;
  [key: string]: any;
}

interface UseMultiAirportOperationalDataResult<T> {
  airports: Array<T & AirportOperationalData>;
  isLoading: boolean;
  errors: MultiAirportOperationalDataFailure[];
}

const getAirportCode = (airport: OperationalAirport): string =>
  (airport.iataCode || airport.code || airport.id || "").trim().toUpperCase();

const cloneEmptyOperationalData = (): AirportOperationalData =>
  JSON.parse(JSON.stringify(EMPTY_AIRPORT_OPERATIONAL_DATA));

/**
 * Enriches airport configuration with card-facing data from the three
 * multi-airport batch APIs after date ranges have been calculated.
 */
export function useMultiAirportOperationalData<T extends OperationalAirport>(
  airports: T[],
  dateRangePayloads: MultiAirportDateRangePayloads,
  enabled: boolean
): UseMultiAirportOperationalDataResult<T> {
  const msalInstance = useMsal().instance as PublicClientApplication;
  const [enrichedAirports, setEnrichedAirports] = useState<
    Array<T & AirportOperationalData>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<MultiAirportOperationalDataFailure[]>(
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    let isCurrentRequest = true;

    if (airports.length === 0) {
      setEnrichedAirports([]);
      setIsLoading(false);
      setErrors([]);
      return () => {
        isCurrentRequest = false;
        controller.abort();
      };
    }

    if (!enabled) {
      setIsLoading(false);
      return () => {
        isCurrentRequest = false;
        controller.abort();
      };
    }

    setIsLoading(true);

    const loadOperationalData = async () => {
      try {
        const accessToken = await AuthHelper.getAccessToken(msalInstance);
        const result = await loadMultiAirportOperationalData(
          airports.map(getAirportCode),
          dateRangePayloads,
          accessToken,
          controller.signal
        );

        if (isCurrentRequest && !controller.signal.aborted) {
          setEnrichedAirports(
            airports.map((airport) => ({
              ...airport,
              ...(result.dataByAirport[getAirportCode(airport)] ||
                cloneEmptyOperationalData()),
            }))
          );
          setErrors(result.errors);
        }
      } catch (reason) {
        if (isCurrentRequest && !controller.signal.aborted) {
          const message =
            reason instanceof Error
              ? reason.message
              : "Unable to obtain an access token";
          setEnrichedAirports(
            airports.map((airport) => ({
              ...airport,
              ...cloneEmptyOperationalData(),
            }))
          );
          setErrors(
            airports.map((airport) => ({
              airportCode: getAirportCode(airport),
              target: "all",
              stage: "authentication",
              message,
            }))
          );
        }
      } finally {
        if (isCurrentRequest && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadOperationalData();

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [airports, dateRangePayloads, enabled, msalInstance]);

  return { airports: enrichedAirports, isLoading, errors };
}
